import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import CheckoutSection from './utils/Checkout.Section';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import CheckoutSectionFooter from './utils/Checkout.SectionFooter';
import {
  acceptTermsOfCondition as setAcceptTermsOfCondition,
  acceptNewsLetterSignUp as setAcceptNewsLetterSignUp,
  setStatusSubmitButton,
  setValidationErrors,
  submit,
  submitDone,
  setCheckoutIsSubmitting,
  saveCustomerDetail,
} from '../../Actions/Checkout.action';
import {
  alwaysRequiredSchema,
  businessCustomerDetailsSchema,
  privateCustomerAddressSchema,
  privateCustomerAlternativeAddressSchema,
} from './utils/Checkout.ValidationSchema';
import { translate } from '../../Services/translation';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Checkbox from '../Inputs/Checkbox';
import styled from 'styled-components';
import Button from '../Button';
import { getColor } from '../../styling/mixins/colors';
import { text } from '../../styling/mixins/typography';
import { fontFamilyBold } from "../../styling/mixins/typography";

const StyledCheckoutSectionFooter = styled(CheckoutSectionFooter)`
  display: flex;
  margin-top: 2.4rem;

  button, .submit-button:hover {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${getColor('primary')};
    ${text('medium')}
    ${fontFamilyBold};
    letter-spacing: 0.5px;
    text-transform: uppercase;
    border: none;
  }
`;

const ConfirmOrder = () => {
  const dispatch = useDispatch();
  const checkout = useTypedSelector((state) => state.checkout);
  const { payload, validationErrors = {}, checkoutIsSubmitting } = checkout;
  const {
    customerDetails,
    alternativeAddress,
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    selectedDeliveryMethod,
    selectedPaymentMethod,
    showAlternativeAddress,
    isBusinessCustomer,
    acceptTermsOfCondition,
    acceptNewsLetterSignUp,
  } = payload;

  const onSubmit = useCallback(() => dispatch(submit()), [dispatch]);

  const placeOrder = useCallback(async () => {
    dispatch(setCheckoutIsSubmitting(true));
    const notCustomerDetailFields = [
      'selectedDeliveryAddressId',
      'selectedBillingAddressId',
      'selectedPaymentMethod',
      'selectedDeliveryMethod',
      'acceptTermsOfCondition',
      'acceptNewsLetterSignUp',
    ];

    const onValidationFailed = (stateKey: string, errors: any[]) => {
      const key = (err: any) =>
        stateKey && !notCustomerDetailFields.includes(err.path)
          ? `${stateKey}-${err.path}`
          : err.path;

      const newValidationErrors = errors.reduce(
        (prev, err) => ({
          ...prev,
          [key(err)]: err.errors,
        }),
        {}
      );

      console.log("validation errors:", newValidationErrors);

      dispatch(
        setValidationErrors({ ...validationErrors, ...newValidationErrors })
      );
      dispatch(setStatusSubmitButton(true));
      dispatch(submitDone(null));
    };

    dispatch(setStatusSubmitButton(false));

    // Validate, then submit. Not proud of this validation, but better than Litium default
      try {
      if (isBusinessCustomer) {
        // Business customer
          try {
          await businessCustomerDetailsSchema.validate(
            {
              ...customerDetails,
              selectedDeliveryAddressId,
              selectedBillingAddressId,
            },
            { abortEarly: false }
          );
          } catch (ex) {
          // re-throw the error with the scheme's state key
          throw { stateKey: 'customerDetails', error: ex.inner };
        }
      } else {
        // Private customer
        try {
          await privateCustomerAddressSchema.validate(
            {
              ...customerDetails,
            },
            { abortEarly: false }
          );
        } catch (ex) {
          // re-throw the error with the scheme's state key
          throw { stateKey: 'customerDetails', error: ex.inner };
        }

        // Alternative address
        if (showAlternativeAddress) {
          try {
            await privateCustomerAlternativeAddressSchema.validate(
              {
                ...alternativeAddress,
              },
              { abortEarly: false }
            );
          } catch (ex) {
            // re-throw the error with the scheme's state key
            throw { stateKey: 'alternativeAddress', error: ex.inner };
          }
        }
      }

      // Always required
      try {
        await alwaysRequiredSchema.validate(
          {
            acceptTermsOfCondition,
            selectedPaymentMethod,
            selectedDeliveryMethod,
          },
          { abortEarly: false }
        );
      } catch (ex) {
        // re-throw the error with the scheme's state key
        throw { stateKey: null, error: ex.inner };
      }

      // Will not be reached if any validation failed
      dispatch(saveCustomerDetail()).then(() => onSubmit());
    } catch (err) {
      dispatch(setCheckoutIsSubmitting(false));
      onValidationFailed(err.stateKey, err.error);
    }
  }, [
    dispatch,
    validationErrors,
    isBusinessCustomer,
    customerDetails,
    selectedDeliveryAddressId,
    selectedBillingAddressId,
    showAlternativeAddress,
    alternativeAddress,
    acceptTermsOfCondition,
    acceptNewsLetterSignUp,
    selectedPaymentMethod,
    selectedDeliveryMethod,
    onSubmit,
  ]);

  const acceptTermsError = useMemo(
    () =>
      validationErrors['acceptTermsOfCondition']
        ? validationErrors['acceptTermsOfCondition'][0]
        : null,
    [validationErrors]
  );

  return (
    <CheckoutSection title={translate('checkout.confirmorder.title')}>
      <CheckoutSectionContent>
        <Checkbox
          inputSize="large"
          id="terms-acceptnews"
          checked={acceptNewsLetterSignUp}
          onChange={(e) =>
            dispatch(setAcceptNewsLetterSignUp(e.target.checked))
          }
          inputState='default'
          label={translate(`checkout.terms.accept.news`)}
          wrapperClassName="confirm-order-checkbox"
        />
        <Checkbox
          inputSize="large"
          id="terms-acceptterms"
          checked={acceptTermsOfCondition}
          onChange={(e) =>
            dispatch(setAcceptTermsOfCondition(e.target.checked))
          }
          inputState={acceptTermsError ? 'error' : 'default'}
          label={
            <>
              {translate(`checkout.terms.acceptterms`)}{' '}
              <a href={payload.termsUrl} className="link">
                {translate(`checkout.terms.link`)}
              </a>
            </>
          }
          message={acceptTermsError}
          wrapperClassName="confirm-order-checkbox"
        />
      </CheckoutSectionContent>
      <StyledCheckoutSectionFooter>
        <Button
          btnType="primary"
          btnSize="xlarge"
          className="submit-button"
          disabled={checkoutIsSubmitting}
          onClick={() => placeOrder()}
          type="submit"
        >
          {!checkoutIsSubmitting && translate('checkout.placeorder')}
          {
            checkoutIsSubmitting && (
              <>{translate('general.loading')}...</>
            ) /* TODO Loading spinner or similar */
          }
        </Button>
      </StyledCheckoutSectionFooter>
    </CheckoutSection>
  );
};

export default ConfirmOrder;
